import { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Save, AlertCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";

interface VideoStat {
  videoId: number;
  title: string;
  categoryName: string;
  completedCount: number;
}
interface Row {
  id: number;
  day: string;
  userId: number;
  username: string;
  displayName: string | null;
  videoId: number;
  videoTitle: string;
}

export default function AdminCheckinsTab() {
  const [videos, setVideos] = useState<VideoStat[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [ratio, setRatio] = useState(0.9);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  function load() {
    setLoading(true);
    setErr(null);
    Promise.all([api.adminCheckins(), api.adminGetSettings()])
      .then(([c, s]) => {
        setVideos(c.videos);
        setRows(c.rows);
        setTotalStudents(c.totalStudents);
        setEnabled(s.checkinEnabled);
        setRatio(s.completeRatio);
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function saveSettings() {
    setSaving(true);
    setSaved(false);
    try {
      await api.adminUpdateSettings({ checkinEnabled: enabled, completeRatio: ratio });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function exportExcel() {
    const data = rows.map((r) => ({
      完成日期: r.day,
      用户名: r.username,
      姓名: r.displayName || "",
      完成视频: r.videoTitle,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "完成记录");
    XLSX.writeFile(wb, `mvg-checkins-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  const overall = useMemo(() => {
    const done = rows.length;
    const target = videos.length * totalStudents;
    return { done, target, pct: target > 0 ? Math.round((done / target) * 100) : 0 };
  }, [rows, videos, totalStudents]);

  return (
    <>
      <div className="card mb-6 p-5">
        <div className="mb-3 text-sm font-semibold text-ink-900">打卡设置</div>
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <label className="flex items-center gap-2 text-sm text-ink-700 md:pb-2.5">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            启用打卡
          </label>
          <label className="w-72">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">
              看完判定：实际观看时长 ≥ 视频时长 × {(ratio * 100).toFixed(0)}%
            </span>
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.05}
              value={ratio}
              onChange={(e) => setRatio(Number(e.target.value))}
              className="w-full"
            />
          </label>
          <button className="btn-primary md:w-auto" onClick={saveSettings} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? "保存中…" : saved ? "已保存 ✓" : "保存设置"}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-ink-400">
          判定基于「真实累计观看时长」——仅播放中且页面可见时计入；拉进度条到结尾不算完成。
        </p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KPI label="视频总数" value={`${videos.length}`} />
        <KPI label="学员人数" value={`${totalStudents}`} />
        <KPI label="总体完成率" value={`${overall.pct}%`} sub={`${overall.done} / ${overall.target}`} />
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      <div className="mb-3 flex items-center gap-3">
        <div className="text-sm font-semibold text-ink-900">各视频完成情况</div>
        <div className="flex-1" />
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-md border border-surface-border bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
        >
          <RefreshCw className="h-3.5 w-3.5" /> 刷新
        </button>
        <button
          onClick={exportExcel}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-surface-border bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" /> 导出明细
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-surface-border bg-white">
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-ink-400">加载中…</div>
        ) : videos.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-ink-400">还没有视频</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-400">
              <tr>
                <th className="px-5 py-3 text-left">分类</th>
                <th className="px-5 py-3 text-left">视频</th>
                <th className="px-5 py-3 text-left">已完成人数</th>
                <th className="px-5 py-3 text-left">完成率</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {videos.map((v) => {
                const rate = totalStudents > 0 ? Math.round((v.completedCount / totalStudents) * 100) : 0;
                return (
                  <tr key={v.videoId} className="hover:bg-surface-muted/40">
                    <td className="px-5 py-3 text-ink-400">{v.categoryName}</td>
                    <td className="px-5 py-3 font-medium text-ink-900">{v.title}</td>
                    <td className="px-5 py-3 text-ink-700">{v.completedCount}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-muted">
                          <div
                            className={`h-full ${rate >= 80 ? "bg-emerald-500" : rate >= 40 ? "bg-brand" : "bg-amber-400"}`}
                            style={{ width: `${Math.min(100, rate)}%` }}
                          />
                        </div>
                        <span className="text-xs text-ink-400">{rate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function KPI({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card p-5">
      <div className="text-xs text-ink-400">{label}</div>
      <div className="font-display text-2xl font-semibold text-ink-900">{value}</div>
      {sub && <div className="text-[11px] text-ink-400">{sub}</div>}
    </div>
  );
}
